import { Component, JSX } from 'react';

import { MetricEventName } from '../../../types/metric-events';
import { emitTyped } from '../../../utils/metric-events-typed';
import { COPY } from '../copy';
import { PICKER_TAB_ID, pickerTabHref } from './picker-tab-href';
import { notePickerOpenSource } from '../state/picker-url';
import styles from './picker-nudge.component.module.scss';

/** §8: `FRAMEPICKER_OPENED.source` values this banner can produce. */
export type PickerNudgeOpenSource = 'nudge' | 'nudge_thumb';

/** One of the four thumbnails at `duration × k/4` (§6.1), already decoded by the caller. */
export interface PickerNudgeThumb {
    /** Source time in seconds. */
    t: number;
    /** An object/blob URL the caller owns and revokes. */
    url: string;
    /** `h:mm:ss.mmm` or shorter, for the caption and the name. */
    label: string;
}

export interface PickerNudgeProps {
    /**
     * Four thumbs when the file is client-decodable, else empty — then only the copy lines and
     * the "Pick the frame" button render (§6.1). Decoding is not this component's business:
     * `media/frame-source.ts` owns it and the page hands the results down.
     */
    thumbs: readonly PickerNudgeThumb[];
    /**
     * Open the picker. `t` is set for a thumbnail click (the tab opens with the playhead
     * there); absent for the button. The OWNER writes the URL and records `FRAMEPICKER_OPENED`
     * on arrival — this component only asks, exactly like the Studio's banner.
     */
    onOpen: (source: PickerNudgeOpenSource, t?: number) => void;
    /** The user said no. Gone for the rest of the visit. */
    onDismiss: () => void;
}

/**
 * The picker's own event shapes for the two names §8 gives it. The names are wire names the
 * Studio ALSO emits, with `{files}` (`studio-nudge.component.tsx`); §8 gives the picker's
 * `{hasThumbs}` and nothing. Same name, two shapes, no discriminator — recorded in the W6 report.
 * `emitTyped` needs no cast here because both names are in `MetricEventName` already.
 */
type PickerNudgeEvents = {
    [MetricEventName.NUDGE_SHOWN]: { hasThumbs: boolean };
    /** IT1 (W3, saas #2): the acceptance itself -- the numerator of the nudge SLO was permanently 0 without it. */
    [MetricEventName.NUDGE_ACCEPTED]: { hasThumbs: boolean };
    [MetricEventName.NUDGE_DISMISSED]: Record<string, never>;
};

/**
 * "Want a specific frame? Pick it on the video." — the single-video nudge on Quick convert
 * (FRAME_PICKER_DESIGN.md §6.1 "Entry points", §8's `NUDGE_*` funnel).
 *
 * WHAT IT IS NOT. Not the Studio's banner (`components/studio/nudge/`): that one fires for 2+
 * still images on image→video pages and offers a different editor; this one fires for exactly
 * ONE staged video on a video→still page. Owner rule: no coupling between editors, so this is
 * its own class with its own copy, and the Studio's file is not touched. It also renders no
 * "Pick it on the video" link — that link lives ABOVE the Advanced Options accordion, not in the
 * nudge (§6.1), and is `PickOnVideoLinkComponent` next door.
 *
 * WHO DECIDES WHEN. The owner (`file-convert.component.tsx`): exactly one staged file, the page
 * gate (`fromMedia === 'VIDEO' && toMedia === 'IMAGE'`, `toType ∉ {gif, apng}`,
 * `featureFlags.framePicker !== false`), never auto-opens, once per visit. A banner that is not
 * on screen must not be counted, so `NUDGE_SHOWN` fires here on mount — the one place that knows
 * the banner is actually rendered — and `NUDGE_DISMISSED` on the close control. There is no
 * ACCEPTED: §8 measures acceptance as `FRAMEPICKER_OPENED{nudge|nudge_thumb} / NUDGE_SHOWN`, and
 * OPENED is recorded on arrival by the picker root (a departure event would count opens that
 * never opened — the `reedit.ts` ruling).
 *
 * A BANNER with `role="status"`, for the reasons the Studio's banner gives: a toast's timer
 * would make DISMISSED meaningless, and an offer must never cut across the upload announcement.
 *
 * LOOK (picker-fix Q12, mock `EntryTab.png`): a card row in the site palette through the picker
 * kit — thumbs · lead + sub line · the ONE primary "Pick the frame" · a ghost "No thanks". No
 * Bootstrap classes: the `alert-info` blue and `btn-primary` came from the page's Bootstrap, not
 * the design system. Words come from `copy.ts`.
 *
 * "Convert first frame" (§6.1) is the page's own Convert button relabelled while the context
 * still holds `screenshotTime = 0`; it is the page's button, so it is not here.
 */
export class PickerNudgeComponent extends Component<PickerNudgeProps> {
    componentDidMount() {
        emitTyped<PickerNudgeEvents, MetricEventName.NUDGE_SHOWN>(MetricEventName.NUDGE_SHOWN, {
            hasThumbs: this.props.thumbs.length > 0,
        });
    }

    /**
     * IT1 (W3, saas #2): the acceptance is recorded HERE (`NUDGE_ACCEPTED{hasThumbs}`), and the source of the open is
     * noted for the owner's `openPickerTab()` (`state/picker-url.ts#notePickerOpenSource`) so the root's
     * `FRAMEPICKER_OPENED{source}` reads `nudge` / `nudge_thumb` -- it read nothing at all for either.
     */
    private onPick = () => {
        this.accept('nudge');
        this.props.onOpen('nudge');
    };

    private onThumb = (t: number) => {
        this.accept('nudge_thumb');
        this.props.onOpen('nudge_thumb', t);
    };

    private accept(source: PickerNudgeOpenSource) {
        emitTyped<PickerNudgeEvents, MetricEventName.NUDGE_ACCEPTED>(MetricEventName.NUDGE_ACCEPTED, { hasThumbs: this.props.thumbs.length > 0 });
        notePickerOpenSource(source);
    }

    private onDismiss = () => {
        emitTyped<PickerNudgeEvents, MetricEventName.NUDGE_DISMISSED>(MetricEventName.NUDGE_DISMISSED, {});
        this.props.onDismiss();
    };

    private renderThumbs(): JSX.Element | null {
        const { thumbs } = this.props;
        if (thumbs.length === 0) return null;
        return (
            <div className={styles['thumbs']} role="list" aria-label={COPY.nudgeThumbs} data-testid="picker-nudge-thumbs">
                {thumbs.map((thumb, i) => (
                    <div role="listitem" key={`${thumb.t}-${i}`}>
                        <button
                            type="button"
                            className={styles['thumb']}
                            onClick={() => this.onThumb(thumb.t)}
                            aria-label={COPY.nudgeThumbName(thumb.label)}
                            data-testid="picker-nudge-thumb"
                            data-t={thumb.t}
                        >
                            <img src={thumb.url} alt="" draggable={false} />
                            <span className={styles['thumb-caption']} aria-hidden="true">{thumb.label}</span>
                        </button>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        return (
            <div
                className={styles['nudge']}
                role="status"
                data-testid="picker-nudge"
            >
                {this.renderThumbs()}
                <div className={styles['copy']}>
                    <span className={styles['lead']}>{COPY.nudgeLead}</span>
                    <span className={styles['sub']}>{COPY.nudgeSub}</span>
                </div>
                <span className={styles['actions']}>
                    <button
                        type="button"
                        className={styles['pick']}
                        data-testid="picker-nudge-pick"
                        onClick={this.onPick}
                    >
                        {COPY.pickTheFrame}
                    </button>
                    <button
                        type="button"
                        className={styles['dismiss']}
                        data-testid="picker-nudge-dismiss"
                        onClick={this.onDismiss}
                        aria-label={COPY.nudgeDismiss}
                    >
                        {COPY.noThanks}
                    </button>
                </span>
            </div>
        );
    }
}

export default PickerNudgeComponent;

/** Re-exported so a caller that renders the banner has the tab id and href builder in one import. */
export { PICKER_TAB_ID, pickerTabHref };
